import React, { ChangeEvent } from 'react';
import FileUpload from 'components/v2/file-upload/FileUpload';
import { HelpBox } from 'components/v2/help-boxes';
import { HyperLink } from 'styles/Styles.styled';
import { ErrorDetails, FieldPanelFullWidth } from 'styles/v2/Styles.styled';
import { ButtonUploadIcon } from 'components/v2/icons';
import { EPD_LCA_TEMPLATES_URL } from 'constants/constants';
import { useImportLcaResultsFile } from 'services/api/mutations';
import TextWithAnimatedEllipsis from 'components/v2/file-upload/TextWithAnimatedEllipsis';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { toaster } from 'components/v2/toast';
import { LCAResultsOutdatedTemplateDialog } from 'components/v2/dialogs/LCAResultsOutdatedTemplateDialog';
import { LcaXLValidationErrorCode } from 'types/lca-results/LcaXLValidationErrorCode';
import { useNotifyLcaImported } from '../../grids/lca-results/util/LcaImportContext';

interface Props {
  epdVersionId: string;
  hasSomeResultsAlready: boolean;
}

const ImportLcaResults: React.FC<Props> = ({ epdVersionId, hasSomeResultsAlready }) => {
  const notifyImported = useNotifyLcaImported();
  const importLcaResultsFileMutation = useImportLcaResultsFile(epdVersionId, notifyImported);

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const file = target && target.files && target.files[0];
    if (!file) {
      return null;
    }

    if (hasSomeResultsAlready) {
      const agreeToContinue = await new Promise((resolve) => {
        confirmAlert({
          caption: 'Replacing LCA results',
          description:
            'Uploading a new file will replace the current data. Any unsaved changes will be lost. Do you want to proceed?',
          onConfirm: () => resolve(true),
          onReject: () => resolve(false),
          acceptLabel: 'Confirm',
        });
      });
      if (!agreeToContinue) {
        return;
      }
    }

    const formData = new FormData();
    formData.append('File', file, file.name);

    try {
      await importLcaResultsFileMutation.mutateAsync(formData);

      toaster({ severity: 'success', summary: 'Imported LCA results', details: 'LCA results are successfully imported.' });
    } catch (ex: any) {
      const errorCode = ex?.response?.data?.code;
      if (errorCode === LcaXLValidationErrorCode.OUTDATED_TEMPLATE) {
        LCAResultsOutdatedTemplateDialog();
        return;
      }

      toaster({
        severity: 'error',
        ...getErrorDetails(errorCode),
      });
    }
  };

  const getErrorDetails = (code: string) => {
    switch (code) {
      case LcaXLValidationErrorCode.INVALID_TEMPLATE:
        return {
          summary: 'The file couldn’t be processed.',
          details: (
            <ErrorDetails>
              Ensure that the file is created using the latest version of the template and try uploading it again.
            </ErrorDetails>
          ),
        };
      case LcaXLValidationErrorCode.INVALID_MODULE_VALUE:
      case LcaXLValidationErrorCode.INVALID_MODULE_COLUMNS:
      case LcaXLValidationErrorCode.INVALID_VALUE_MAX_LENGTH:
        return {
          summary: 'Character limit exceeded in the uploaded file.',
          details: <ErrorDetails>Please review the instructions, update the file, and try again.</ErrorDetails>,
        };
      default:
        return {
          summary: 'Something went wrong while processing your file.',
          details: <ErrorDetails>Please try again or contact support if the issue persists.</ErrorDetails>,
        };
    }
  };

  return (
    <FieldPanelFullWidth style={{ gridRowGap: 16 }}>
      <div>
        <HelpBox>
          <span>
            LCA results can be entered manually into the tables below or automatically populated from an Excel file using the
            "Import LCA results" function.
            <span style={{ display: 'block', marginTop: 8 }}>
              Ensure that the <strong>latest version of the "Excel template for PCR 2019:14"</strong> from{' '}
              <HyperLink target="_blank" rel="noopener noreferrer" href={EPD_LCA_TEMPLATES_URL}>
                environdec.com
              </HyperLink>{' '}
              is used, as older versions are not accepted.
            </span>
          </span>
        </HelpBox>
        <FileUpload
          label="Import LCA results"
          icon={ButtonUploadIcon}
          accept=".xlsx"
          validExtensions={['xlsx']}
          name="LCA"
          onFileUpload={uploadFile}
          disabled={importLcaResultsFileMutation.isPending}
        />
      </div>
      {importLcaResultsFileMutation.isPending && <TextWithAnimatedEllipsis>Uploading file</TextWithAnimatedEllipsis>}
    </FieldPanelFullWidth>
  );
};

export default ImportLcaResults;
